import React, { useEffect, useState } from "react";
import { Box, useMediaQuery } from "@mui/material";
import Sidebar from "./Sidebar";
import Header from "./Header";
import { DRAWER_WIDTH, COLLAPSED_WIDTH } from "./layoutConstants";
import { Outlet, useLocation } from "react-router-dom";
import TopLoadingBar from "../ui/TopLoadingBar";
import PageTransition from "../ui/PageTransition";
// Admin shares the Manager/portal design system: the same brand tokens
// (portalTokens.css), typography/flourishes (portalBrand.css) and table
// system (portalTable.css). adminTeal*.css then alias the historical
// --admin-* variables onto those tokens (see theme/adminTeal.css).
import "../../theme/portalTokens.css";
import "../../theme/portalBrand.css";
import "../../theme/portalTable.css";
import "../../theme/adminTeal.css";
import "../../theme/adminTealOverrides.css";
import "../../theme/adminTealModal.css";
import "../../theme/adminShell.css";
import "../../theme/adminHero.css";
import "../../theme/adminStats.css";
import "../../theme/adminModal.css";
import "../../theme/adminActions.css";
import "../../theme/adminToast.css";
import "../../theme/adminFormActions.css";
// Last on purpose: one shared look for text buttons, tabs and the row-action
// column across every Admin page (see the header comment in that file).
import "../../theme/adminButtons.css";

// Slug of the current Admin page (first path segment after /admin; a few
// sections need the second segment to tell their pages apart). It is only
// stamped on <main> as `data-admin-page` so theme/adminHero.css can pick the
// right icon for the page header -- nothing else reads it.
function adminPageSlug(pathname) {
  const parts = String(pathname || "").split("/").filter(Boolean).slice(1);
  const [first = "home", second] = parts;
  if (["inventory", "accounts", "access-management"].includes(first) && second) return `${first}-${second}`;
  if (first === "tasks" && second === "create") return "tasks-create";
  return first;
}

export default function Layout() {
  const { pathname } = useLocation();
  const isMobile = useMediaQuery("(max-width:899px)");

  // Mobile: drawer starts closed. Desktop: starts open.
  const [open, setOpen] = useState(!isMobile);

  useEffect(() => {
    setOpen(!isMobile);
  }, [isMobile]);

  // Popups (react-bootstrap <Modal>) are portaled to <body>, outside this
  // layout's root, so they cannot inherit the Admin theme from an ancestor.
  // Flag <body> while the Admin shell is mounted; theme/adminModal.css keys
  // off it. Nothing else reads this class.
  useEffect(() => {
    document.body.classList.add("admin-shell-active");
    return () => document.body.classList.remove("admin-shell-active");
  }, []);

  const left = open ? DRAWER_WIDTH : COLLAPSED_WIDTH;

  // On smaller screens, navigating to a page collapses the sidebar back
  // down so the page content isn't left obscured behind an open drawer.
  const handleSidebarNavigate = () => {
    if (isMobile) setOpen(false);
  };

  return (
    <Box
      className="portal-theme admin-teal-theme"
      sx={{ display: "flex", minHeight: "100vh", backgroundColor: "var(--page-bg, #faf7f1)" }}
    >
      <TopLoadingBar />
      <Sidebar isOpen={open} onNavigate={handleSidebarNavigate} onClose={() => setOpen(false)} />
      <Header isSidebarOpen={open} onToggleSidebar={() => setOpen((v) => !v)} />
      {/* Same content frame as the Manager shell: identical gutters, so every
          Admin page shares one left/right edge. Vertical offset for the fixed
          AppBar is `main`'s own top padding (a Toolbar spacer would become a
          separate flex item and open a gap beside the Sidebar). */}
      <Box
        component="main"
        className="admin-shell-main"
        data-admin-page={adminPageSlug(pathname)}
        sx={{
          flexGrow: 1,
          // A flex child's automatic min-width is its content's min-content
          // size unless overridden -- without this, a wide descendant (e.g.
          // the Staff table) can force this box, and therefore the whole
          // layout, wider than the viewport instead of letting its own
          // overflow-x:auto wrapper scroll internally.
          minWidth: 0,
          width: { xs: "100%", md: `calc(100% - ${left}px)` },
          // Toolbar height (62 / 68 / 72px) + a 16-24px breathing gap.
          pt: { xs: "78px", sm: "84px", md: "96px" },
          px: { xs: 1.5, sm: 2, md: 3 },
          pb: 3,
          minHeight: "100vh",
          overflowX: "hidden",
          backgroundColor: "var(--page-bg, #faf7f1)",
          transition: (theme) =>
            theme.transitions.create(["width", "margin-left"], {
              easing: theme.transitions.easing.sharp,
              duration: theme.transitions.duration.shorter,
            }),
        }}
      >
        <PageTransition>
          <Outlet />
        </PageTransition>
      </Box>
    </Box>
  );
}
